import {
    ActionComponent,
    ActionComponentDefinition,
    ComponentThatCanDisableOptions,
    ComponentWithOptions,
    InputComponent,
    LabeledComponentDefinition,
    ValueComponentDefinition,
} from "components/components/Component";
import { InputBaseComponent } from "components/components/InputBaseComponent";
import { Tooltip } from "components/components/tooltip/tooltip";
import { ComponentActionHelper } from "components/events/componentActionHelper";
import { fireDataChangedEvent, fireInitDataChangedEvent } from "components/form/dataChangedEventDetail";
import { FocusManager } from "components/pages/focusManager";
import { OptionIterator } from "components/input/select/OptionIterator";
import $ from "jquery";
import { findFirstOption, isOption, Optgroup, Option } from "../options";
import optionsTemplate from "components/input/select/options.hbs";
import template from "./select.hbs";
import "components/input/select/select/select.scss";

export class SelectInputWithoutS6 extends InputBaseComponent<SelectInputDefinition>
    implements InputComponent, ComponentWithOptions, ActionComponent, ComponentThatCanDisableOptions {
    private options: Array<Option | Optgroup>;
    private selectElement: HTMLSelectElement;
    public static componentType = "selectInputWithoutS6";
    private valueHasBeenInitialized = false;
    private readonly: boolean = false;
    private visited: boolean = false;
    private valid: boolean = true;
    private validationMessage: string = null;

    constructor(schema: SelectInputDefinition) {
        super(schema);
        if (schema.labelKey == null || schema.labelKey.length === 0) {
            this.definition.labelWidth = 0;
        }
        if (this.definition.emptyWidth == null) {
            this.definition.emptyWidth = 0;
        }
        if (this.definition.inputWidth == null) {
            this.definition.inputWidth = 16 - (this.definition.labelWidth || 0) - this.definition.emptyWidth;
        }

        if (this.definition.options != null) {
            this.options = this.definition.options;
        }
        if (this.definition.width == null) {
            this.definition.width = 16;
        }
    }

    public render(): Element {
        let jqueryView = $(template(this.definition));
        this.view = jqueryView[0];
        this.selectElement = this.view.querySelector("select");
        this.bindEvents();
        ComponentActionHelper.register(this);
        Tooltip.bindElement(this.view.querySelector(".o-form-component__input-group"));
        return this.view;
    }

    private bindEvents(): void {
        let jqueryView = $(this.view);
        this.selectElement.addEventListener("change", () => {
            fireDataChangedEvent(this, this.view, this.getValue());
        });
        this.selectElement.addEventListener("focus", () => {
            this.visited = true;
        });
        if (this.definition.tabOnEnter === true) {
            this.selectElement.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    e.stopPropagation();
                    e.preventDefault();
                    FocusManager.focusNext();
                }
            });
        }
    }

    public getValue(): string | number {
        if (this.options == null || this.options.length === 0) {
            return null;
        }
        let idx = this.selectElement.selectedIndex;
        let allOptions = OptionIterator.filterFlat(
            this.options,
            () => true);
        if (idx !== -1) {
            return allOptions[idx].value;
        }

        return null;
    }

    public value(v: string | number) {
        if (v == null) {
            const nullOption = (this.options ?? []).find((o) => isOption(o) && o.value === null);
            if (nullOption == null) {
                this.setDefaultValue();
            } else {
                $(this.selectElement).val(null);
            }
        } else {
            $(this.selectElement).val(v);
        }
        this.valueHasBeenInitialized = true;
    }

    public setReadOnly(ro: boolean) {
        this.readonly = ro;
        if (ro) {
            this.selectElement.disabled = true;
            this.selectElement.classList.remove("c-select-input__input__enabled");
            this.selectElement.classList.add("c-select-input__input__disabled");
        } else {
            this.selectElement.disabled = false;
            this.selectElement.classList.remove("c-select-input__input__disabled");
            this.selectElement.classList.add("c-select-input__input__enabled");
        }
    }

    public setRequired(required: boolean) {
        if (required) {
            $(this.view).find("label").addClass("is-required");
        } else {
            $(this.view).find("label").removeClass("is-required");
        }
    }

    public setValidationStatus(valid: boolean, message?: string) {
        this.valid = valid;
        this.validationMessage = message || null;
        if (valid) {
            this.selectElement.classList.remove("is-pending");
            this.selectElement.classList.remove("has-error");
            $(this.view).find(".o-form-component__input-group").removeAttr("data-tip");
        } else {
            if (this.visited) {
                this.selectElement.classList.add("has-error");
                this.selectElement.classList.remove("is-pending");
            } else {
                this.selectElement.classList.remove("has-error");
                this.selectElement.classList.add("is-pending");
            }
        }
        $(this.view).find(".o-form-component__input-group").attr("data-tip", message);
    }

    public addOptions(options: Array<Option | Optgroup>): void {
        if (options == null) {
            return;
        }
        if (this.options == null) {
            this.options = [];
        }
        this.options = this.options.concat(options);
        let select = $(this.view).find("select");
        let templated = $(optionsTemplate({ options, codeInOptionLabel: this.definition.codeInOptionLabel }));
        templated.appendTo(select as JQuery<HTMLElement>);
    }

    public setOptions(options: Array<Option | Optgroup>): void {
        $(this.view).find("select")[0].innerHTML = "";
        this.options.length = 0;
        this.addOptions(options);
    }

    public postRender(): Node {
        if (!this.valueHasBeenInitialized) {
            this.setDefaultValue();
        }
        return this.view;
    }

    private setDefaultValue() {
        if (this.options != null && this.options.length > 0) {
            this.value(findFirstOption(this.options).value);
            if (null !== this.getValue() && "" !== this.getValue()) {
                fireDataChangedEvent(this, this.view, this.getValue());
            } else {
                fireInitDataChangedEvent(this, this.view, this.getValue());
            }
        }
    }

    public focus(): void {
        this.selectElement.focus();
    }

    public enableOptions(options: Array<string | number>) {
        for (const optionValue of options ?? []) {
            (this.view.querySelector("option[value=" + optionValue + "]") as HTMLOptionElement).disabled = false;
        }
    }

    public disableOptions(options: Array<string | number>) {
        if (options.includes(this.getValue())) {
            this.value("");
            fireDataChangedEvent(this, this.view, this.getValue());
        }
        for (const optionValue of options ?? []) {
            (this.view.querySelector("option[value=" + optionValue + "]") as HTMLOptionElement).disabled = true;
        }
    }
}

export interface SelectInputDefinition
    extends LabeledComponentDefinition, ValueComponentDefinition, ActionComponentDefinition {
    inputWidth?: number;
    emptyWidth?: number;
    codeInOptionLabel?: boolean;
    options?: Array<Option | Optgroup>;
    tabOnEnter?: boolean;
}
